/**
 * WorkflowBuilder
 *
 * Full-screen dialog — master/detail layout:
 *  Left rail: ordered stage list with add/remove/reorder
 *  Right panel: edit stage name, description, fields (via StageFieldEditor)
 *  Monitoring stage: special card, no fields
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, TextField, Button, IconButton,
  List, ListItemButton, ListItemText, ListItemSecondaryAction,
  Divider, Chip, Paper, Tooltip, Select, MenuItem,
  FormControl, InputLabel, Alert, CircularProgress,
  Checkbox, FormControlLabel,
} from '@mui/material';
import {
  ArrowLeft, ArrowUp, ArrowDown, Trash2, Plus,
  BarChart2, Save, X, Eye, AlertCircle, Calendar,
} from 'lucide-react';
import StageFieldEditor from './StageFieldEditor';
import DynamicFieldRenderer from '../Forms/DynamicFieldRenderer';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import BASE_URL from '../../config';

const emptyStage = (pos) => ({
  _id: Math.random().toString(36).slice(2),
  name: '',
  description: '',
  position: pos,
  stage_type: 'standard',
  fields: [],
});

const ASSET_TYPES = [
  { id: null, name: 'Any' },
  { id: 1, name: 'Tree / Eco' },
  { id: 2, name: 'Lake' },
];

const WorkflowBuilder = ({ open, templateId, organisationId, onClose, onSaved }) => {
  const { getToken } = useAuth();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [assetTypeId, setAssetTypeId] = useState(null);
  const [enforceSequential, setEnforceSequential] = useState(true);
  const [stages, setStages] = useState([]);
  const [selectedStageId, setSelectedStageId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [contracts, setContracts] = useState([]);
  const [showMaintenancePanel, setShowMaintenancePanel] = useState(false);
  const [maintenanceEnabled, setMaintenanceEnabled] = useState(false);
  // One entry per standing plan the handover creates (e.g. weekly maintenance + monthly reporting)
  const [maintenancePlans, setMaintenancePlans] = useState([]);

  const newMaintenancePlan = (overrides = {}) => ({
    _id: Math.random().toString(36).slice(2),
    name_suffix: 'Maintenance',
    frequency: 'weekly',
    interval: 1,
    weekdays: [],
    lead_days: 3,
    until: '',
    contract_id: '',
    activities: [],
    ...overrides,
  });

  const specToPlanState = (spec) => newMaintenancePlan({
    name_suffix: spec?.name_suffix || 'Maintenance',
    frequency: spec?.recurrence?.frequency || 'weekly',
    interval: spec?.recurrence?.interval || 1,
    weekdays: spec?.recurrence?.weekdays || [],
    lead_days: spec?.recurrence?.lead_days ?? 3,
    until: spec?.recurrence?.until || '',
    contract_id: spec?.contract_id || '',
    activities: (spec?.activities || []).map(f => ({ ...f, _id: Math.random().toString(36).slice(2) })),
  });

  const updateMaintenancePlan = (planId, key, value) => {
    setMaintenancePlans(prev => prev.map(p => p._id === planId ? { ...p, [key]: value } : p));
    markDirty();
  };

  const selectedStage = stages.find(s => s._id === selectedStageId) || null;

  // Load existing template
  useEffect(() => {
    if (!open) return;
    setError('');
    if (!templateId) {
      setName('');
      setDescription('');
      setAssetTypeId(null);
      setEnforceSequential(true);
      setMaintenanceEnabled(false);
      setMaintenancePlans([]);
      const first = emptyStage(1);
      setStages([first]);
      setSelectedStageId(first._id);
      setIsDirty(false);
      return;
    }

    setLoading(true);
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        if (!token || cancelled) return;
        const res = await axios.get(`${BASE_URL}/api/workflow-templates/${templateId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        const t = res.data;
        setName(t.name || '');
        setDescription(t.description || '');
        setAssetTypeId(t.asset_type_id || null);
        setEnforceSequential(t.enforce_sequential ?? true);
        // maintenance_spec: single spec object (legacy) or array of specs
        const specEntries = Array.isArray(t.maintenance_spec)
          ? t.maintenance_spec
          : t.maintenance_spec ? [t.maintenance_spec] : [];
        setMaintenanceEnabled(specEntries.length > 0);
        setMaintenancePlans(specEntries.map(specToPlanState));
        const loaded = (t.stages || []).map(s => ({
          ...s,
          _id: s.id || Math.random().toString(36).slice(2),
          fields: (s.fields || []).map(f => ({
            ...f,
            _id: f.id || Math.random().toString(36).slice(2),
            options: Array.isArray(f.options) ? f.options : (f.options ? JSON.parse(f.options) : []),
          })),
        }));
        setStages(loaded);
        setSelectedStageId(loaded[0]?._id || null);
        setIsDirty(false);
      } catch (err) {
        console.error(err);
        if (!cancelled) setError('Failed to load template');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, templateId, getToken]);

  // Load org's contracts for the per-stage contract selector (public templates skip this)
  useEffect(() => {
    if (!open || !organisationId) { setContracts([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        if (!token || cancelled) return;
        const res = await axios.get(`${BASE_URL}/api/contractdropdown`, {
          params: { organisation_id: organisationId },
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setContracts(res.data || []);
      } catch (err) {
        console.error(err);
      }
    })();
    return () => { cancelled = true; };
  }, [open, organisationId, getToken]);

  const markDirty = () => setIsDirty(true);

  const addStage = () => {
    const pos = stages.length + 1;
    const s = emptyStage(pos);
    setStages(prev => [...prev, s]);
    setSelectedStageId(s._id);
    markDirty();
  };

  const removeStage = (id) => {
    const remaining = stages.filter(s => s._id !== id).map((s, i) => ({ ...s, position: i + 1 }));
    setStages(remaining);
    setSelectedStageId(remaining[0]?._id || null);
    markDirty();
  };

  const moveStage = (id, dir) => {
    const idx = stages.findIndex(s => s._id === id);
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= stages.length) return;
    const arr = [...stages];
    [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
    setStages(arr.map((s, i) => ({ ...s, position: i + 1 })));
    markDirty();
  };

  const updateStage = (id, key, value) => {
    setStages(prev => prev.map(s => s._id === id ? { ...s, [key]: value } : s));
    markDirty();
  };

  const validate = () => {
    if (!name.trim()) return 'Workflow name is required.';
    if (stages.length === 0) return 'At least one stage is required.';
    for (const s of stages) {
      if (!s.name.trim()) return 'Every stage must have a name.';
      if (s.stage_type === 'standard') {
        const labels = s.fields.map(f => f.label.trim().toLowerCase()).filter(Boolean);
        if (new Set(labels).size !== labels.length) return `Stage "${s.name}": field labels must be unique.`;
        for (const f of s.fields) {
          if (['select','multiselect'].includes(f.field_type) && (!f.options || f.options.length === 0)) {
            return `Stage "${s.name}": field "${f.label}" must have at least one option.`;
          }
        }
      }
    }
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    setError('');
    setSaving(true);

    const payload = {
      organisation_id: organisationId,
      name,
      description,
      asset_type_id: assetTypeId,
      enforce_sequential: enforceSequential,
      maintenance_spec: maintenanceEnabled && maintenancePlans.length ? maintenancePlans.map(p => ({
        name_suffix: p.name_suffix,
        recurrence: {
          frequency: p.frequency,
          interval: Number(p.interval) || 1,
          weekdays: p.frequency === 'weekly' && p.weekdays.length ? p.weekdays : undefined,
          lead_days: Number(p.lead_days) || 0,
          until: p.until || undefined,
        },
        activities: (p.activities || []).map((f, j) => ({
          field_key: f.field_key || slugify(f.label),
          label: f.label,
          field_type: f.field_type,
          options: f.options?.length ? f.options : null,
          is_required: f.is_required,
          position: j + 1,
        })),
        contract_id: p.contract_id || null,
      })) : null,
      stages: stages.map((s, i) => ({
        name: s.name,
        description: s.description,
        position: i + 1,
        stage_type: s.stage_type,
        expected_days: s.expected_days || 30,
        contract_id: s.contract_id || null,
        fields: (s.fields || []).map((f, j) => ({
          field_key: f.field_key || slugify(f.label),
          label: f.label,
          field_type: f.field_type,
          options: f.options?.length ? f.options : null,
          is_required: f.is_required,
          help_text: f.help_text || null,
          position: j + 1,
        })),
      })),
    };

    try {
      const token = await getToken();
      if (!token) return;
      if (templateId) {
        await axios.put(`${BASE_URL}/api/workflow-templates/${templateId}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        await axios.post(`${BASE_URL}/api/workflow-templates`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      setIsDirty(false);
      onSaved?.();
      onClose();
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Failed to save workflow');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (isDirty && !window.confirm('You have unsaved changes. Discard them?')) return;
    onClose();
  };

  if (!open) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xl" fullWidth
      PaperProps={{ sx: { height: '92vh', display: 'flex', flexDirection: 'column' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid', borderColor: 'divider', py: 1.5 }}>
        <IconButton size="small" onClick={handleClose}><ArrowLeft size={18} /></IconButton>
        <TextField
          value={name}
          onChange={e => { setName(e.target.value); markDirty(); }}
          placeholder="Workflow name…"
          variant="standard"
          sx={{ flex: 1, mr: 2 }}
          inputProps={{ style: { fontWeight: 600, fontSize: 16 } }}
        />
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Asset type</InputLabel>
          <Select value={assetTypeId ?? ''} label="Asset type"
            onChange={e => { setAssetTypeId(e.target.value === '' ? null : e.target.value); markDirty(); }}>
            {ASSET_TYPES.map(t => (
              <MenuItem key={String(t.id)} value={t.id ?? ''}>{t.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControlLabel
          sx={{ ml: 1, mr: 0 }}
          control={
            <Checkbox
              size="small"
              checked={enforceSequential}
              onChange={e => { setEnforceSequential(e.target.checked); markDirty(); }}
            />
          }
          label={<Typography variant="body2">Enforce stage order</Typography>}
        />
        <Tooltip title="Changes only apply to tasks created after saving"><AlertCircle size={16} color="#888" style={{ marginLeft: 4 }} /></Tooltip>
        <Button
          variant="contained" size="small" startIcon={saving ? <CircularProgress size={14} /> : <Save size={14} />}
          onClick={handleSave} disabled={saving} sx={{ ml: 1 }}
        >
          Save
        </Button>
      </DialogTitle>

      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mx: 2, mt: 1 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          {/* Left rail — stage list */}
          <Box sx={{ width: 240, borderRight: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column' }}>
            <Typography variant="caption" color="text.secondary" sx={{ px: 2, pt: 1.5, pb: 0.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Stages
            </Typography>
            <List dense sx={{ flex: 1, overflowY: 'auto' }}>
              {stages.map((stage, idx) => (
                <ListItemButton
                  key={stage._id}
                  selected={stage._id === selectedStageId}
                  onClick={() => { setSelectedStageId(stage._id); setShowMaintenancePanel(false); }}
                  sx={{ pr: 6 }}
                >
                  {stage.stage_type === 'monitoring'
                    ? <BarChart2 size={14} style={{ marginRight: 6, color: '#5B8DEF', flexShrink: 0 }} />
                    : <Typography variant="caption" sx={{ mr: 1, minWidth: 18, color: 'text.disabled', flexShrink: 0 }}>{idx + 1}</Typography>
                  }
                  <ListItemText
                    primary={stage.name || <em style={{ color: '#aaa' }}>Unnamed stage</em>}
                    secondary={stage.stage_type === 'monitoring' ? 'Monitoring' : `${(stage.fields || []).length} fields`}
                    primaryTypographyProps={{ fontSize: 13, noWrap: true }}
                    secondaryTypographyProps={{ fontSize: 11 }}
                  />
                  <ListItemSecondaryAction sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Tooltip title="Move up"><span>
                      <IconButton edge="end" size="small" disabled={idx === 0} onClick={(e) => { e.stopPropagation(); moveStage(stage._id, -1); }}>
                        <ArrowUp size={12} />
                      </IconButton>
                    </span></Tooltip>
                    <Tooltip title="Move down"><span>
                      <IconButton edge="end" size="small" disabled={idx === stages.length - 1} onClick={(e) => { e.stopPropagation(); moveStage(stage._id, 1); }}>
                        <ArrowDown size={12} />
                      </IconButton>
                    </span></Tooltip>
                  </ListItemSecondaryAction>
                </ListItemButton>
              ))}
            </List>
            <Box
              onClick={() => { setSelectedStageId(null); setShowMaintenancePanel(true); markDirty(); }}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1,
                borderTop: '1px solid', borderBottom: '1px solid', borderColor: 'divider',
                cursor: 'pointer',
                bgcolor: showMaintenancePanel ? 'action.selected' : 'transparent',
                borderLeft: maintenanceEnabled ? '3px solid' : '3px solid transparent',
                borderLeftColor: maintenanceEnabled ? 'secondary.main' : 'transparent',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Calendar size={14} style={{ color: '#5B8DEF', flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontSize: 13, fontWeight: 500 }} noWrap>Maintenance handover</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                  {maintenanceEnabled
                    ? `${maintenancePlans.length} plan${maintenancePlans.length === 1 ? '' : 's'} configured`
                    : 'Off'}
                </Typography>
              </Box>
              {maintenanceEnabled && <Chip label="On" size="small" color="secondary" sx={{ height: 18, fontSize: 10 }} />}
            </Box>
            <Divider />
            <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Button size="small" startIcon={<Plus size={14} />} onClick={() => { setShowMaintenancePanel(false); addStage(); }} variant="outlined" fullWidth>
                Add stage
              </Button>
            </Box>
          </Box>

          {/* Right panel — stage editor */}
          <Box sx={{ flex: 1, p: 3, overflowY: 'auto' }}>
            {showMaintenancePanel ? (
              <Box>
                <Typography variant="h6" sx={{ mb: 1 }}>Maintenance handover</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  When enabled, completing this workflow's final stage automatically creates a standing
                  maintenance plan that spawns recurring visit tasks (e.g. watering, monitoring).
                </Typography>
                <FormControlLabel
                  control={<Checkbox checked={maintenanceEnabled} onChange={e => {
                    setMaintenanceEnabled(e.target.checked);
                    if (e.target.checked && maintenancePlans.length === 0) setMaintenancePlans([newMaintenancePlan()]);
                    markDirty();
                  }} />}
                  label="Enable maintenance handover"
                  sx={{ mb: 2 }}
                />
                {maintenanceEnabled && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {maintenancePlans.map((plan, planIdx) => (
                      <Paper key={plan._id} variant="outlined" sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                          <Typography variant="subtitle2">Plan {planIdx + 1}: {plan.name_suffix || 'Untitled'}</Typography>
                          <Tooltip title="Remove this plan"><span>
                            <IconButton size="small" color="error" disabled={maintenancePlans.length === 1}
                              onClick={() => { setMaintenancePlans(prev => prev.filter(p => p._id !== plan._id)); markDirty(); }}>
                              <Trash2 size={14} />
                            </IconButton>
                          </span></Tooltip>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <TextField
                            label="Plan name suffix" size="small"
                            value={plan.name_suffix}
                            onChange={e => updateMaintenancePlan(plan._id, 'name_suffix', e.target.value)}
                            helperText='Plan is named "<task title> — <suffix>"'
                          />
                          <Divider><Typography variant="caption">Recurrence</Typography></Divider>
                          <Box sx={{ display: 'flex', gap: 2 }}>
                            <FormControl size="small" sx={{ flex: 1 }}>
                              <InputLabel>Frequency</InputLabel>
                              <Select value={plan.frequency} label="Frequency" onChange={e => updateMaintenancePlan(plan._id, 'frequency', e.target.value)}>
                                <MenuItem value="daily">Daily</MenuItem>
                                <MenuItem value="weekly">Weekly</MenuItem>
                                <MenuItem value="monthly">Monthly</MenuItem>
                              </Select>
                            </FormControl>
                            <TextField label="Every N" type="number" size="small" sx={{ width: 100 }}
                              value={plan.interval} onChange={e => updateMaintenancePlan(plan._id, 'interval', e.target.value)} inputProps={{ min: 1 }} />
                            <TextField label="Lead days" type="number" size="small" sx={{ width: 110 }}
                              value={plan.lead_days} onChange={e => updateMaintenancePlan(plan._id, 'lead_days', e.target.value)} inputProps={{ min: 0 }} />
                          </Box>
                          {plan.frequency === 'weekly' && (
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map((label, idx) => (
                                <Chip key={idx} label={label} size="small"
                                  color={plan.weekdays.includes(idx) ? 'primary' : 'default'}
                                  onClick={() => updateMaintenancePlan(plan._id, 'weekdays',
                                    plan.weekdays.includes(idx) ? plan.weekdays.filter(x => x !== idx) : [...plan.weekdays, idx])} />
                              ))}
                            </Box>
                          )}
                          <TextField label="Until (optional)" type="date" size="small" value={plan.until}
                            onChange={e => updateMaintenancePlan(plan._id, 'until', e.target.value)} InputLabelProps={{ shrink: true }} />

                          <Divider><Typography variant="caption">Assignment</Typography></Divider>
                          <FormControl size="small" fullWidth disabled={!organisationId}>
                            <InputLabel>Contract (optional)</InputLabel>
                            <Select value={plan.contract_id} label="Contract (optional)" onChange={e => updateMaintenancePlan(plan._id, 'contract_id', e.target.value)}>
                              <MenuItem value="">None</MenuItem>
                              {contracts.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                            </Select>
                          </FormControl>
                          {!organisationId && (
                            <Typography variant="caption" color="text.secondary">Public templates cannot carry a maintenance contract.</Typography>
                          )}

                          <Divider><Typography variant="caption">Visit checklist</Typography></Divider>
                          <StageFieldEditor fields={plan.activities} onChange={fields => updateMaintenancePlan(plan._id, 'activities', fields)} />
                        </Box>
                      </Paper>
                    ))}
                    <Button size="small" variant="outlined" startIcon={<Plus size={14} />}
                      onClick={() => { setMaintenancePlans(prev => [...prev, newMaintenancePlan({ name_suffix: 'Reporting', frequency: 'monthly' })]); markDirty(); }}>
                      Add another plan (e.g. monthly reporting)
                    </Button>
                  </Box>
                )}
              </Box>
            ) : !selectedStage ? (
              <Typography color="text.secondary">Select or add a stage to edit.</Typography>
            ) : selectedStage.stage_type === 'monitoring' ? (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BarChart2 size={20} color="#5B8DEF" /> Monitoring & Impact Assessment
                  </Typography>
                  <IconButton size="small" color="error" onClick={() => removeStage(selectedStage._id)}>
                    <Trash2 size={16} />
                  </IconButton>
                </Box>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'info.50', borderColor: 'info.200' }}>
                  <Typography variant="body2" color="info.dark">
                    This stage links to the live Monitoring &amp; Impact Assessment data for the task's site.
                    It has no custom fields — when a task executor reaches this stage, they see the existing
                    site metrics dashboard and can deep-link to <code>/ecological</code> or <code>/lake</code>.
                    Drag it to the desired position in the stage list.
                  </Typography>
                </Paper>
              </Box>
            ) : (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography variant="h6">Stage: {selectedStage.name || 'Untitled'}</Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title={preview ? 'Back to editor' : 'Preview form'}>
                      <IconButton size="small" onClick={() => setPreview(!preview)}>
                        <Eye size={16} />
                      </IconButton>
                    </Tooltip>
                    <IconButton size="small" color="error" onClick={() => removeStage(selectedStage._id)}>
                      <Trash2 size={16} />
                    </IconButton>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                  <TextField
                    label="Stage name"
                    value={selectedStage.name}
                    onChange={e => updateStage(selectedStage._id, 'name', e.target.value)}
                    size="small"
                    sx={{ flex: 1, minWidth: 200 }}
                    required
                  />
                  <TextField
                    label="Description (optional)"
                    value={selectedStage.description || ''}
                    onChange={e => updateStage(selectedStage._id, 'description', e.target.value)}
                    size="small"
                    sx={{ flex: 2, minWidth: 260 }}
                  />
                  <TextField
                    label="Expected days"
                    type="number"
                    value={selectedStage.expected_days ?? 30}
                    onChange={e => updateStage(selectedStage._id, 'expected_days', Math.max(1, Number(e.target.value) || 1))}
                    size="small"
                    sx={{ width: 130 }}
                    inputProps={{ min: 1 }}
                  />
                </Box>

                <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <FormControl size="small" sx={{ minWidth: 220 }} disabled={!organisationId}>
                    <InputLabel>Contract (optional)</InputLabel>
                    <Select
                      value={selectedStage.contract_id || ''}
                      label="Contract (optional)"
                      onChange={e => updateStage(selectedStage._id, 'contract_id', e.target.value || null)}
                    >
                      <MenuItem value="">None</MenuItem>
                      {contracts.map(c => (
                        <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {selectedStage.contract_id && (
                    <Typography variant="body2" color="text.secondary">
                      Provider: {contracts.find(c => c.id === selectedStage.contract_id)?.service_provider_name || '—'}
                    </Typography>
                  )}
                  {!organisationId && (
                    <Typography variant="caption" color="text.secondary">
                      Public templates cannot carry a contract.
                    </Typography>
                  )}
                </Box>

                <Divider sx={{ mb: 2 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2">Fields</Typography>
                  {preview && <Chip label="Preview mode" size="small" color="info" />}
                </Box>

                {preview ? (
                  <DynamicFieldRenderer
                    fields={selectedStage.fields || []}
                    values={{}}
                    readOnly
                  />
                ) : (
                  <StageFieldEditor
                    fields={selectedStage.fields || []}
                    onChange={fields => updateStage(selectedStage._id, 'fields', fields)}
                  />
                )}
              </Box>
            )}
          </Box>
        </Box>
      )}

      <DialogActions sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
        <TextField
          label="Description"
          value={description}
          onChange={e => { setDescription(e.target.value); markDirty(); }}
          size="small"
          sx={{ flex: 1, mr: 2 }}
          placeholder="Brief description of this workflow…"
        />
        <Button onClick={handleClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}
          startIcon={saving ? <CircularProgress size={14} /> : <Save size={14} />}>
          Save workflow
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const slugify = (str) =>
  (str || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');

export default WorkflowBuilder;
